import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { TripPermissionsService } from '../../../core/services/trip-permissions.service';
import { AppSection, canAccessSection } from '../../../core/models/section-permissions.model';
import { ThemeService } from '../../../core/services/theme.service';
import { Workspace } from '../../../core/models/workspace.model';
import { SolutionOption } from '../../../core/models/solution.model';
import { appendWorkspaceId } from '../../../core/utils/solution-catalog.util';
import { SwitchWorkspaceModal } from '../modalbase/switch-workspace-modal/switch-workspace-modal';
import { SwitchSolutionModal } from '../modalbase/switch-solution-modal/switch-solution-modal';

const DEFAULT_AVATAR = 'assets/images/Generic_avatar_LT.png';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    TranslateModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    MatMenuModule,
    MatDividerModule,
    MatToolbarModule,
    MatRippleModule,
    RouterLink,
    RouterLinkActive,
  ],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  host: {
    '(window:resize)': 'onResize()'
  }
})
export class Navbar {
  protected translate: TranslateService = inject(TranslateService);
  protected theme: ThemeService = inject(ThemeService);
  protected authService = inject(AuthService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private permissions = inject(TripPermissionsService);

  constructor() {
    this.permissions.resolveRole().pipe(takeUntilDestroyed()).subscribe();
  }

  /**
   * Whether the role may see a section's menu entry.
   *
   * Hidden until the role has resolved, not just while it reads 'unknown': the signal starts
   * there, so a role without access never sees an entry flash in and out, while an
   * administrator's appear a moment after load. The trip and custom reports are open to
   * every role and are not gated at all.
   */
  protected canSee(section: AppSection): boolean {
    return this.permissions.resolved() && canAccessSection(this.permissions.role(), section);
  }

  /** El item "Cambiar de workspace" solo existe si el usuario pertenece a más de uno. */
  protected canSwitchWorkspace = this.authService.canSwitchWorkspace;

  protected isMobile = signal<boolean>(window.innerWidth <= 768);
  protected menuOpen = signal<boolean>(false);

  protected toggleMenu() {
    this.menuOpen.update((open) => !open);
  }

  protected onResize() {
    const wasMobile = this.isMobile();
    const currentMobile = window.innerWidth <= 768;
    if (wasMobile !== currentMobile) {
      this.isMobile.set(currentMobile);
      this.menuOpen.set(false);
    }
  }

  protected isRail = computed<boolean>(() => !this.isMobile() && !this.menuOpen());

  // Expansion panel states
  logisticsExpanded = signal(false);
  workforceExpanded = signal(false);
  clientsExpanded = signal(false);
  reportsExpanded = signal(false);

  handlePanelClick(panel: 'logistics' | 'workforce' | 'clients' | 'reports', event: MouseEvent) {
    if (this.isRail()) {
      if (panel === 'logistics') this.logisticsExpanded.set(!this.logisticsExpanded());
      else if (panel === 'workforce') this.workforceExpanded.set(!this.workforceExpanded());
      else if (panel === 'clients') this.clientsExpanded.set(!this.clientsExpanded());
      else if (panel === 'reports') this.reportsExpanded.set(!this.reportsExpanded());
    }
  }

  protected userName = computed<string>(() => this.authService.currentUser()?.displayName || 'Invitado');
  protected userRole = computed<string>(() => this.authService.currentUser()?.email || 'General');
  protected avatarUrl = computed<string>(
    () => this.authService.profilePhotoUrl() ?? DEFAULT_AVATAR
  );

  // The stored path can outlive the file on the media host; fall back rather than
  // leaving a broken image in the footer.
  protected onAvatarError(event: Event): void {
    (event.target as HTMLImageElement).src = DEFAULT_AVATAR;
  }

  /**
   * Soluciones del workspace activo. Se abre desde el ítem "Soluciones" del menú y desde
   * su equivalente del pie cuando el menú está contraído.
   */
  protected openSwitchSolutionModal(): void {
    if (this.isMobile()) {
      this.toggleMenu();
    }

    this.dialog
      .open(SwitchSolutionModal, {
        width: '560px',
        maxWidth: '92vw',
        autoFocus: false
      })
      .afterClosed()
      .subscribe((selected: SolutionOption | undefined) => {
        if (!selected?.url) return;

        // El workspace viaja en la url: la solución de destino comparte la cookie de
        // sesión del dominio, así que con el id entra directa y sin pedir credenciales.
        const target = appendWorkspaceId(selected.url, this.authService.getWorkspaceId() ?? null);
        window.open(target, '_blank', 'noopener,noreferrer');
      });
  }

  protected openSwitchWorkspaceModal(): void {
    const workspaces = this.authService.getAvailableWorkspaces();
    if (workspaces.length <= 1) return;

    const activeWorkspaceId = this.authService.getActiveWorkspaceId();

    this.dialog
      .open(SwitchWorkspaceModal, {
        width: '560px',
        maxWidth: '92vw',
        autoFocus: false,
        data: { workspaces, activeWorkspaceId }
      })
      .afterClosed()
      .subscribe((selected: Workspace | undefined) => {
        if (!selected?.id || selected.id === activeWorkspaceId) return;

        this.authService.setActiveWorkspaceId(selected.id);
        // Cada pantalla lee el workspace de forma imperativa al cargarse, así que navegar
        // sin más dejaría en pantalla listas y filtros del workspace anterior. Recargar en
        // el panel principal es lo único que garantiza que toda la app vuelva a consultar
        // con el workspace nuevo.
        this.router.navigate(['/dashboard']).then(() => window.location.reload());
      });
  }

  async logout() {
    await this.authService.logout();
    this.router.navigate(['/login']);
  }
}